import { useEffect, useMemo, useRef, useState } from 'react';
import { merge, type Observable } from 'rxjs';
import { combineLatestWith, distinctUntilChanged, filter, map, pairwise, shareReplay } from 'rxjs/operators';

import { isEqual } from 'lodash-es';
import { useMarketAccountsContext, useMarketsContext } from '../contexts';
import { MARKET_DATA_SNAPSHOT, TRADE_DIRECTION } from '../tokens';
import type {
  MarketDataSnapshot,
  MarketDataSnapshotRequest,
  TradeDirection,
  TradeDirectionRequest,
} from '../types/MarketDataSnapshot';
import type { SubscriptionResponse } from '../types/SubscriptionResponse';
import {
  ConnectionStatusEnum,
  DepthTypeEnum,
  MarketAccountTypeEnum,
  type FeeModeEnum,
  type LiquidityTypeEnum,
} from '../types/types';
import { EMPTY_ARRAY, isKeyIn } from '../utils';
import { useBehaviorSubject } from './useBehaviorSubject';
import { useObservable } from './useObservable';
import { useSecurity } from './useSecurity';
import { useSubscription } from './useSubscription';

/** A market (account) name that can be sent for the MarketDataSnapshot subscription */
type TradeableMarketAccountOrMarketName = string & { __type: 'TradeableMarketAccountOrMarketName' };

/**
 * Takes a list of market names and market account names, and returns only the ones that are valid to send to `useMarketDataSnapshot`.
 *
 * The MarketDataSnapshot subscription will throw an error if `markets` contains any market accounts that have `Type` !== `Trading`.
 * It will also throw an error if `markets` contains any markets that don't exist.
 *
 * @param marketsAndMarketAccounts A combined list of market names and market account names
 * @return Only those markets / market accounts which can be passed to `useMarketDataSnapshot`
 */
export function useMarketsAndMarketAccountsForMarketDataSnapshot(
  marketsAndMarketAccounts: string[]
): TradeableMarketAccountOrMarketName[] {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();

  const validMarketAccounts = useRef<TradeableMarketAccountOrMarketName[]>([]);

  useEffect(() => {
    const newValue = marketsAndMarketAccounts.filter(
      (market): market is TradeableMarketAccountOrMarketName =>
        marketsByName?.has(market) || marketAccountsByName?.get(market)?.Type === MarketAccountTypeEnum.Trading
    );
    if (!isEqual(validMarketAccounts.current, newValue)) {
      validMarketAccounts.current = newValue;
    }
  }, [marketAccountsByName, marketsAndMarketAccounts, marketsByName]);
  return validMarketAccounts.current;
}

export function useMarketDataSnapshot({
  currency,
  baseCurrency = currency,
  depth,
  includeTradeDirection,
  priceIncrement,
  quantity,
  markets,
  sizeBuckets,
  symbol,
  tag,
  feeMode,
  liquidityType,
  throttle,
}: UseMarketDataSnapshotProps) {
  const [request, setRequest] = useState<(MarketDataSnapshotRequest | TradeDirectionRequest)[] | null>(null);
  const security = useSecurity(symbol);

  useEffect(() => {
    if (security != null && (depth || quantity || sizeBuckets)) {
      const marketDataRequest: MarketDataSnapshotRequest = {
        name: MARKET_DATA_SNAPSHOT,
        DepthType: DepthTypeEnum.Price,
        PriceIncrement: priceIncrement,
        Symbol: security.Symbol,
        FeeMode: feeMode,
        LiquidityType: liquidityType,
        tag: tag,
        Markets: markets,
        Throttle: throttle,
      };
      if (depth != null) {
        marketDataRequest.Depth = depth;
      } else {
        if (currency && baseCurrency && currency !== baseCurrency && quantity != null) {
          marketDataRequest.AmountBuckets = [quantity];
        } else {
          marketDataRequest.SizeBuckets = quantity ? [quantity] : sizeBuckets;
        }
      }
      const requests: (MarketDataSnapshotRequest | TradeDirectionRequest)[] = [marketDataRequest];
      if (includeTradeDirection) {
        requests.push({
          name: TRADE_DIRECTION,
          Symbol: security.Symbol,
          Markets: markets,
          tag: tag,
        });
      }
      setRequest(requests);
    } else {
      setRequest(null);
    }
  }, [
    baseCurrency,
    currency,
    priceIncrement,
    security,
    depth,
    tag,
    sizeBuckets,
    quantity,
    feeMode,
    liquidityType,
    includeTradeDirection,
    markets,
    throttle,
  ]);

  const { observable: symbolObservable } = useBehaviorSubject(() => symbol, [symbol]);
  const { data: subscription } = useSubscription<SubscriptionResponse<MarketDataSnapshot | TradeDirection>>(request, {
    unsubscribeOnPageInvisibility: true,
  });

  const marketDataSnapshots = useObservable(
    () =>
      subscription.pipe(
        filter(
          (json): json is SubscriptionResponse<MarketDataSnapshot> =>
            json.type === MARKET_DATA_SNAPSHOT && (isKeyIn('Bids', json.data[0]) || isKeyIn('Offers', json.data[0]))
        ),
        // TODO: Revise this logic as it's mostly the same as ava/useMarketDataSnapshot except the wrapper object
        map(json => json.data[0]),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [subscription]
  );

  const unstaleMarketDataSnapshots = useMemo(
    () => clearStaleMarketDataOnSymbolChange(marketDataSnapshots, symbolObservable),
    [marketDataSnapshots, symbolObservable]
  );

  const tradeDirections = useObservable(
    () =>
      subscription.pipe(
        filter((json): json is SubscriptionResponse<TradeDirection> => json.type === TRADE_DIRECTION),
        map(json => json.data[0]),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [subscription]
  );

  return {
    marketDataSnapshots: unstaleMarketDataSnapshots,
    tradeDirections,
  };
}

/**
 * Clears stale market data when the selected symbol changes.
 *
 * Snapshots that arrive for a previously selected symbol are dropped so they
 * never reach consumers. Because the upstream `marketDataSnapshots` observable
 * is recreated on symbol change (losing its replayed value), an empty snapshot
 * is emitted the instant the symbol changes so consumers clear stale prices
 * until data for the new symbol arrives.
 */
export function clearStaleMarketDataOnSymbolChange(
  marketDataSnapshots: Observable<MarketDataSnapshot>,
  symbolObservable: Observable<string | undefined>
): Observable<MarketDataSnapshot> {
  // Only let through snapshots that belong to the currently selected symbol.
  const snapshotsForCurrentSymbol = marketDataSnapshots.pipe(
    combineLatestWith(symbolObservable),
    filter(([snapshot, currentSymbol]) => snapshot.Symbol === currentSymbol),
    map(([snapshot]) => snapshot)
  );

  // Whenever the symbol changes after the first one (including when it's cleared
  // to undefined), emit an empty snapshot so stale prices clear before the next
  // symbol's data arrives. When the symbol is cleared, the empty book stays keyed
  // to the symbol it replaced.
  const emptySnapshotOnSymbolChange = symbolObservable.pipe(
    distinctUntilChanged(),
    pairwise(),
    filter((symbols): symbols is [string, string | undefined] => symbols[0] !== undefined),
    map(([previousSymbol, currentSymbol]) => buildEmptyMarketDataSnapshot(currentSymbol ?? previousSymbol))
  );

  return merge(emptySnapshotOnSymbolChange, snapshotsForCurrentSymbol);
}

function buildEmptyMarketDataSnapshot(symbol: string): MarketDataSnapshot {
  return {
    Symbol: symbol,
    Bids: EMPTY_ARRAY,
    Offers: EMPTY_ARRAY,
    Status: ConnectionStatusEnum.Offline,
    DepthType: DepthTypeEnum.Price,
  };
}

type UseMarketDataSnapshotProps = {
  currency?: string;
  baseCurrency?: string;
  depth?: number;
  includeTradeDirection?: boolean;
  priceIncrement?: string;
  quantity?: string;
  sizeBuckets?: string[];
  symbol?: string;
  tag: string;
  feeMode?: FeeModeEnum;
  liquidityType?: LiquidityTypeEnum;
  /**
   * If you are passing `markets`, filter them with the useMarketsAndMarketAccountsForMarketDataSnapshot function first
   */
  markets?: TradeableMarketAccountOrMarketName[];
  throttle?: string;
};
